import type { RaidResult } from "../protocol/messages.js";

/**
 * What survives a raid: experience, four skills and a lifetime tally. Gear is
 * what a raid pays; this is what it teaches, and it is never lost. XP comes
 * from EVENTS (kills, an extraction, loot value, a quest) and levels are a
 * gate; SKILLS come from USE (sprinting, carrying, being shot, firing), grow
 * with diminishing returns and buy small simulation multipliers (`SkillMods`).
 * Pure, total and deterministic like `economy/quests.ts`;
 * `applyRaidToProgress` is one transaction beside the quest settlement.
 */

// ---------------------------------------------------------------------------
// The skills
// ---------------------------------------------------------------------------

/**
 * The four things a raid can teach, each attached to a verb a player performs
 * anyway: endurance (sprinting → faster stamina), strength (kilograms carried
 * → capacity), vitality (damage taken → bleeds less), gunsmith (rounds fired
 * → guns wear slower).
 */
export type SkillId = "endurance" | "strength" | "vitality" | "gunsmith";

/**
 * Every skill, in the fixed order everything walks them (and `createProgress`
 * writes keys), so JSON key order matches across the wire.
 */
export const SKILL_IDS: readonly SkillId[] = ["endurance", "strength", "vitality", "gunsmith"];

/** A skill is a percentage, and 100 is the ceiling of every one of them. */
export const SKILL_MAX = 100;

// ---------------------------------------------------------------------------
// The state
// ---------------------------------------------------------------------------

/** The tally, since the account was created. Display only — nothing reads
 *  these to make a decision, which is why they can be added to freely. */
export interface LifetimeStats {
  raids: number;
  extracts: number;
  deaths: number;
  mia: number;
  scavKills: number;
  pmcKills: number;
  bossKills: number;
  /**
   * Every point of xp ever earned. Equal to `xp` today, but separate: a spend
   * would move one and not the other.
   */
  xpTotal: number;
}

/** THE progression of one account, whole. Rides on the profile and crosses the
 *  wire verbatim, exactly like `HideoutState`. */
export interface ProgressState {
  /** Running total. Never negative, never reduced. */
  xp: number;
  /** 0..100 each, floats — the fractions are the point, since a single raid
   *  moves a skill by a fraction of a point. */
  skills: Record<SkillId, number>;
  stats: LifetimeStats;
}

/** A brand-new account: level 1, four skills at nothing, an empty tally. */
export function createProgress(): ProgressState {
  return {
    xp: 0,
    skills: { endurance: 0, strength: 0, vitality: 0, gunsmith: 0 },
    stats: {
      raids: 0,
      extracts: 0,
      deaths: 0,
      mia: 0,
      scavKills: 0,
      pmcKills: 0,
      bossKills: 0,
      xpTotal: 0,
    },
  };
}

// ---------------------------------------------------------------------------
// The ladder
// ---------------------------------------------------------------------------

/** The top of the ladder. Everything a level gates is authored against this,
 *  and `validateTraders` / `validateQuests` refuse a gate above it. */
export const MAX_LEVEL = 30;

/**
 * Cumulative xp to REACH each level, index 0 = level 2. Authored as integers
 * generated once from `round(120 * (n - 1)^1.5 * 1.035^(n - 2))` — `Math.pow`
 * is not pinned to the last bit across engines, and a table cannot drift. A
 * gentle power times slow compounding: level 10 at ~4 300, 20 at ~18 500, 30
 * at ~49 000 — about a hundred good raids. Strictly monotone; a test pins it.
 */
export const XP_LEVELS: readonly number[] = [
  120, 351, 668, 1064, 1540, 2095, 2732, 3455, 4266, 5172, 6176, 7283, 8499, 9831, 11285, 12867,
  14585, 16447, 18460, 20634, 22978, 25501, 28214, 31126, 34250, 37597, 41179, 45010, 49103,
];

/** What level `xp` buys, in [1, MAX_LEVEL]. Total: garbage is level 1. */
export function levelFor(xp: number): number {
  const v = typeof xp === "number" && Number.isFinite(xp) && xp > 0 ? xp : 0;
  let level = 1;
  for (const need of XP_LEVELS) {
    if (v < need) break;
    level += 1;
  }
  return level;
}

/**
 * Progress through the current level (`have`/`need` from the level's threshold
 * — a bar with no arithmetic), or `null` at `MAX_LEVEL`.
 */
export function xpToNext(xp: number): { have: number; need: number } | null {
  const v = typeof xp === "number" && Number.isFinite(xp) && xp > 0 ? xp : 0;
  const level = levelFor(v);
  if (level >= MAX_LEVEL) return null;
  const base = level === 1 ? 0 : (XP_LEVELS[level - 2] as number);
  const next = XP_LEVELS[level - 1] as number;
  return { have: v - base, need: next - base };
}

// ---------------------------------------------------------------------------
// What a raid is worth
// ---------------------------------------------------------------------------

/**
 * The xp price list. Getting out is the biggest single number, twice a PMC
 * kill — the game must not teach fighting over exits. The loot term is what
 * makes a fat bag worth carrying past a fight and is forfeit on death. Kills
 * pay whatever the outcome.
 */
export const XP = {
  scavKill: 40,
  pmcKill: 150,
  bossKill: 400,
  /** Paid once, on "extracted". */
  extract: 300,
  /** Per euro of loot value carried out. Only on "extracted". */
  lootPerEuro: 0.1,
  /** What a quest pays when its own `rewardXp` is not authored. */
  questDefault: 200,
} as const;

/**
 * What the raid did with the body, accumulated per tick by the server. Every
 * field is a USE, never an achievement — a skill you raise only by winning
 * rewards those who need it least.
 */
export interface RaidUseGains {
  /** Seconds with the sprint button held and the legs actually moving. */
  sprintSec: number;
  /** Kilogram-seconds: the carried weight integrated over the raid. */
  carriedKgSec: number;
  /** Hit points removed from you, armour and all. */
  damageTaken: number;
  /** Rounds sent. One per trigger pull, not one per pellet. */
  shotsFired: number;
}

/** A raid nobody moved in. The neutral element of every skill sum. */
export function emptyGains(): RaidUseGains {
  return { sprintSec: 0, carriedKgSec: 0, damageTaken: 0, shotsFired: 0 };
}

/**
 * How fast each skill answers its verb — sized so a hard raid moves a fresh
 * skill by ~1.5 points and the ladder is weeks, not an evening.
 */
const SKILL_RATE: Record<SkillId, number> = {
  endurance: 0.05,
  strength: 0.0004,
  vitality: 0.02,
  gunsmith: 0.03,
};

/**
 * What the settlement needs about the raid. Separate from `RaidQuestOutcome`: a
 * quest cares which exit, this cares how many kilograms.
 */
export interface RaidProgressInput {
  result: RaidResult;
  scavKills: number;
  pmcKills: number;
  bossKills: number;
  /** Euro value of everything carried out. Ignored unless "extracted". */
  lootValueEur: number;
  /** Xp from quests completed at this settlement — `QuestSettlementResult`'s
   *  `rewardXp`, and the counter's `applyQuestTurnIn` result at a trade-in. */
  questXp: number;
  gains: RaidUseGains;
  /**
   * A flat multiplier on the whole xp award — the hideout's radio room.
   * Optional; absent, garbage and ≤ 0 read as 1. Not a field on
   * `ProgressState`: it belongs to the hideout and is derived at settlement.
   */
  xpMult?: number;
}

export interface ProgressSettlementResult {
  /** The NEW state. The input is never touched. */
  next: ProgressState;
  xpGained: number;
  levelBefore: number;
  levelAfter: number;
}

/**
 * Settles one raid against one profile's progression. Pure, total,
 * deterministic. Rules in order:
 *  1. the incoming state is sanitized;
 *  2. XP = kills × rate + (extracted ? extract + floor(loot × rate) : 0) +
 *     quest xp, then × `xpMult` and floored — applied LAST to everything, and
 *     skipped entirely (no float) when there is no multiplier;
 *  3. stats: one more raid and result, kills added, `xpTotal` advanced;
 *  4. skills grow by `rate * use / (1 + skill / 50)`, clamped to [0, 100].
 * `levelBefore`/`levelAfter` are returned so the screen and the server do not
 * re-derive them from different totals.
 */
export function applyRaidToProgress(
  p: ProgressState,
  input: RaidProgressInput,
): ProgressSettlementResult {
  const prev = sanitizeProgress(p);

  const extracted = input.result === "extracted";
  const scavKills = asCount(input.scavKills);
  const pmcKills = asCount(input.pmcKills);
  const bossKills = asCount(input.bossKills);

  let xpGained =
    scavKills * XP.scavKill + pmcKills * XP.pmcKill + bossKills * XP.bossKill + asCount(input.questXp);
  if (extracted) {
    xpGained += XP.extract + Math.floor(asAmount(input.lootValueEur) * XP.lootPerEuro);
  }
  // The branch, not `Math.floor(sum * 1)`: a settlement with no Funkraum must
  // not touch a float, so every pre-existing account settles identically.
  const xpMult = asMultiplier(input.xpMult);
  if (xpMult !== 1) xpGained = Math.floor(xpGained * xpMult);

  const xp = prev.xp + xpGained;

  // The struct itself is guarded, not just its fields: `gains` crosses a server
  // boundary as one object, and a settlement must not throw on a missing one.
  const gains: Partial<RaidUseGains> = isRecord(input.gains) ? input.gains : {};
  const skills: Record<SkillId, number> = {
    endurance: grow(prev.skills.endurance, SKILL_RATE.endurance, gains.sprintSec),
    strength: grow(prev.skills.strength, SKILL_RATE.strength, gains.carriedKgSec),
    vitality: grow(prev.skills.vitality, SKILL_RATE.vitality, gains.damageTaken),
    gunsmith: grow(prev.skills.gunsmith, SKILL_RATE.gunsmith, gains.shotsFired),
  };

  const stats: LifetimeStats = {
    raids: prev.stats.raids + 1,
    extracts: prev.stats.extracts + (extracted ? 1 : 0),
    deaths: prev.stats.deaths + (input.result === "died" ? 1 : 0),
    mia: prev.stats.mia + (input.result === "mia" ? 1 : 0),
    scavKills: prev.stats.scavKills + scavKills,
    pmcKills: prev.stats.pmcKills + pmcKills,
    bossKills: prev.stats.bossKills + bossKills,
    xpTotal: prev.stats.xpTotal + xpGained,
  };

  return {
    next: { xp, skills, stats },
    xpGained,
    levelBefore: levelFor(prev.xp),
    levelAfter: levelFor(xp),
  };
}

/**
 * One skill, one raid later. `1 + skill / 50` is the brake (two thirds at 50, a
 * third at 100). Clamped both ends.
 */
function grow(skill: number, rate: number, use: number | undefined): number {
  const cur = clampSkill(skill);
  const u = asAmount(use);
  if (u <= 0) return cur;
  const next = cur + (rate * u) / (1 + cur / 50);
  return next > SKILL_MAX ? SKILL_MAX : next;
}

/**
 * One gym session: `points` into ONE skill via `grow` with rate 1 — the same
 * brake as a raid, so a veteran cannot beat it by choosing the other door.
 * Pure and total; nonsense points move nothing. XP and the tally are
 * untouched — lifting weights is not a raid.
 */
export function trainSkill(p: ProgressState, skill: SkillId, points: number): ProgressState {
  const prev = sanitizeProgress(p);
  const gain = asAmount(points);
  if (gain <= 0) return prev;
  // The id came off the wire; checked against the roster this file owns so
  // `skills[skill] =` never reaches a prototype setter.
  if (!SKILL_IDS.includes(skill)) return prev;
  const skills: Record<SkillId, number> = { ...prev.skills };
  skills[skill] = grow(prev.skills[skill], 1, gain);
  return { xp: prev.xp, skills, stats: prev.stats };
}

// ---------------------------------------------------------------------------
// What the skills buy
// ---------------------------------------------------------------------------

/**
 * The four multipliers a body's skills apply — all 1 at skill 0 and small at
 * 100 (20–30 %, never a doubling): an edge, never an advantage in a straight
 * fight; there is no damage multiplier and will not be. Each has one consumer:
 * `carryMult` → `moveModsFromWeight`; `bleedChanceMult` → `applyHit` (the
 * chance only); `wearMult` → `wearWeapon`; `staminaRegenMult` → `stepMovement`
 * (regen only).
 */
export interface SkillMods {
  carryMult: number;
  bleedChanceMult: number;
  wearMult: number;
  staminaRegenMult: number;
}

/** The neutral body: every multiplier at 1. What a caller with no skills at
 *  hand passes, and what `welcome` means by omitting `skillMods`. */
export const DEFAULT_SKILL_MODS: SkillMods = {
  carryMult: 1,
  bleedChanceMult: 1,
  wearMult: 1,
  staminaRegenMult: 1,
};

/**
 * The multipliers this set of skills earns — linear, no threshold to grind
 * toward. At 100: +20 % carry, −30 % bleed, −30 % wear, +30 % regen. Total.
 */
export function skillMods(skills: Record<SkillId, number>): SkillMods {
  const s: Partial<Record<SkillId, number>> = isRecord(skills) ? skills : {};
  const strength = clampSkill(s.strength);
  const vitality = clampSkill(s.vitality);
  const gunsmith = clampSkill(s.gunsmith);
  const endurance = clampSkill(s.endurance);
  return {
    carryMult: 1 + (0.2 * strength) / SKILL_MAX,
    bleedChanceMult: 1 - (0.3 * vitality) / SKILL_MAX,
    wearMult: 1 - (0.3 * gunsmith) / SKILL_MAX,
    staminaRegenMult: 1 + (0.3 * endurance) / SKILL_MAX,
  };
}

// ---------------------------------------------------------------------------
// Hardening
// ---------------------------------------------------------------------------

/**
 * Turns anything into a usable `ProgressState` — `sanitizeHideout`'s twin,
 * never throws: a non-object is a fresh account, `xp` clamped non-negative,
 * skills into [0, 100], tallies floored, unknown keys dropped. It does NOT
 * reconcile `xpTotal` with `xp` — that would be inventing history.
 */
export function sanitizeProgress(x: unknown): ProgressState {
  const out = createProgress();
  if (typeof x !== "object" || x === null || Array.isArray(x)) return out;
  const raw = x as Record<string, unknown>;

  out.xp = asAmount(own(raw, "xp"));

  const rawSkills = own(raw, "skills");
  if (isRecord(rawSkills)) {
    for (const id of SKILL_IDS) out.skills[id] = clampSkill(own(rawSkills, id));
  }

  const rawStats = own(raw, "stats");
  if (isRecord(rawStats)) {
    out.stats = {
      raids: asCount(own(rawStats, "raids")),
      extracts: asCount(own(rawStats, "extracts")),
      deaths: asCount(own(rawStats, "deaths")),
      mia: asCount(own(rawStats, "mia")),
      scavKills: asCount(own(rawStats, "scavKills")),
      pmcKills: asCount(own(rawStats, "pmcKills")),
      bossKills: asCount(own(rawStats, "bossKills")),
      xpTotal: asCount(own(rawStats, "xpTotal")),
    };
  }

  return out;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Own-property read off an untrusted object, so `progress["toString"]` is
 *  nothing rather than a function — the guard `hideout/state.ts` spells out. */
function own(rec: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(rec, key) ? rec[key] : undefined;
}

/** A stored counter, made safe: integral and non-negative. */
function asCount(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/** A stored quantity, made safe: finite and non-negative, fractions kept. */
function asAmount(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** An experience multiplier, made safe: a finite number above zero, or 1. Only
 *  ever a BONUS — see `RaidProgressInput.xpMult`. */
function asMultiplier(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 1;
  return n;
}

/** A stored skill value, made safe: finite and inside [0, SKILL_MAX]. */
function clampSkill(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 0;
  return n > SKILL_MAX ? SKILL_MAX : n;
}
